import { Spin } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from './auth/useAuth';
import { AppLayout } from './layout/AppLayout';
import { BrandMark } from './layout/BrandMark';
import { AppRoutes } from './routes';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { ContactRequestPage } from './pages/ContactRequestPage';
import { CountryPage } from './pages/CountryPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { CustomerPage } from './pages/CustomerPage';
import { MyTasksPage } from './pages/MyTasksPage';
import { ProjectBoardPage } from './pages/ProjectBoardPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ProjectPage } from './pages/ProjectPage';
import { SalesOpportunityPage } from './pages/SalesOpportunityPage';
import { ShowcaseProjectPage } from './pages/ShowcaseProjectPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostEditorPage } from './pages/BlogPostEditorPage';
import { BlogPreviewPage } from './pages/BlogPreviewPage';
import { ProjectTaskPage } from './pages/ProjectTaskPage';
import { ProjectTemplateDetailPage } from './pages/ProjectTemplateDetailPage';
import { ProjectTemplatePage } from './pages/ProjectTemplatePage';
import { ProjectTypePage } from './pages/ProjectTypePage';
import { ProjectWorkloadPage } from './pages/ProjectWorkloadPage';
import { CurrencyPage } from './pages/CurrencyPage';
import { TaxRatePage } from './pages/TaxRatePage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { UnitOfMeasurePage } from './pages/UnitOfMeasurePage';
import { RolePage } from './pages/RolePage';
import { UserAccountPage } from './pages/UserAccountPage';

/**
 * Gốc của ứng dụng.
 *
 * Bốn trạng thái, và thứ tự xét có chủ ý:
 *
 * 1. Đang thử phục hồi phiên từ cookie — hiển thị vòng chờ. KHÔNG hiển thị trang đăng nhập ở
 *    bước này: người dùng đã đăng nhập sẽ thấy trang đăng nhập nhấp nháy một nhịp rồi biến mất,
 *    và với họ thì trông như hệ thống bị lỗi.
 * 2. Chưa đăng nhập — hiển thị trang đăng nhập.
 * 3. Đã đăng nhập nhưng đang dùng mật khẩu tạm — chỉ cho vào màn hình đổi mật khẩu.
 * 4. Đã đăng nhập bình thường — hiển thị khung bố cục và nội dung theo đường dẫn.
 */
export function App(): ReactNode {
  const { isRestoring, user } = useAuth();

  if (isRestoring) {
    return (
      <div className="erp-splash">
        <BrandMark width={280} />
        <Spin size="large" />
      </div>
    );
  }

  if (user === null) {
    return <LoginPage />;
  }

  /*
    Mật khẩu tạm được đặt lúc cài đặt hệ thống nên có thể đã đi qua nhiều tay. Chặn ở đây thay vì
    chỉ hiển thị một lời nhắc: một lời nhắc bỏ qua được thì mật khẩu tạm sẽ tồn tại rất lâu.

    Vẫn cho phép đăng xuất, vì khung bố cục luôn hiển thị — người dùng không bị kẹt.
  */
  if (user.mustChangePassword) {
    return (
      <AppLayout>
        <ChangePasswordPage />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Routes>
        <Route path={AppRoutes.Home} element={<HomePage />} />
        <Route path={AppRoutes.ChangePassword} element={<ChangePasswordPage />} />
        <Route path={AppRoutes.UserAccount} element={<UserAccountPage />} />
        <Route path={AppRoutes.Role} element={<RolePage />} />
        <Route path={AppRoutes.UnitOfMeasure} element={<UnitOfMeasurePage />} />
        <Route path={AppRoutes.Currency} element={<CurrencyPage />} />
        <Route path={AppRoutes.Country} element={<CountryPage />} />
        <Route path={AppRoutes.TaxRate} element={<TaxRatePage />} />

        {/*
          Tuyến chi tiết khai SAU tuyến danh sách. Thứ tự này không quan trọng với bộ định tuyến hiện
          tại vì hai khuôn không chồng nhau, nhưng giữ thứ tự cụ thể-dần là quy ước để về sau thêm
          những khuôn có phần chung mà không phải rà lại cả danh sách.
        */}
        <Route path={AppRoutes.Opportunity} element={<SalesOpportunityPage />} />

        <Route path={AppRoutes.Customer} element={<CustomerPage />} />
        <Route path={AppRoutes.ContactRequest} element={<ContactRequestPage />} />
        <Route path={AppRoutes.CustomerDetailPattern} element={<CustomerDetailPage />} />

        <Route path={AppRoutes.MyTasks} element={<MyTasksPage />} />
        <Route path={AppRoutes.ProjectType} element={<ProjectTypePage />} />

        {/*
          Mẫu dự án: tuyến danh sách trước, tuyến chi tiết sau — giữ thứ tự cụ thể-dần như hai
          nhóm tuyến ở trên.
        */}
        <Route path={AppRoutes.ProjectTemplate} element={<ProjectTemplatePage />} />
        <Route
          path={AppRoutes.ProjectTemplateDetailPattern}
          element={<ProjectTemplateDetailPage />}
        />
        <Route path={AppRoutes.Project} element={<ProjectPage />} />

        {/*
          Hai tuyến tĩnh khai TRƯỚC tuyến có tham số. Bộ định tuyến hiện tại xếp hạng theo mức cụ
          thể nên thứ tự không đổi kết quả, nhưng giữ thứ tự cụ thể-dần là quy ước: đọc từ trên
          xuống là thấy ngay đường dẫn nào bắt trước.
        */}
        <Route path={AppRoutes.ProjectBoard} element={<ProjectBoardPage />} />
        <Route path={AppRoutes.ProjectWorkload} element={<ProjectWorkloadPage />} />
        <Route path={AppRoutes.ShowcaseProject} element={<ShowcaseProjectPage />} />
        <Route path={AppRoutes.Blog} element={<BlogPage />} />
        <Route path={AppRoutes.BlogNew} element={<BlogPostEditorPage />} />
        <Route path={AppRoutes.BlogPreview} element={<BlogPreviewPage />} />
        <Route path={AppRoutes.BlogEditPattern} element={<BlogPostEditorPage />} />
        <Route path={AppRoutes.ProjectTaskPattern} element={<ProjectTaskPage />} />
        <Route path={AppRoutes.ProjectDetailPattern} element={<ProjectDetailPage />} />

        {/*
          Đường dẫn không có trong hệ thống đưa về trang chủ thay vì hiển thị trang trắng.
          Dùng replace để nút Quay lại của trình duyệt không kẹt ở đường dẫn sai.
        */}
        <Route path="*" element={<Navigate to={AppRoutes.Home} replace />} />
      </Routes>
    </AppLayout>
  );
}
